import React, { useRef } from "react";
import "./DeliveryBill.css";
import logo from "../Assets/fab hub logo (tm).png";

const statusColor = {
  Pending: "#d97706",
  Confirmed: "#0ea5e9",
  Packed: "#6b7280",
  Shipped: "#7c3aed",
  Delivered: "#16a34a",
  Cancelled: "#ef4444",
};

const DeliveryBill = ({ order, onClose }) => {
  const billRef = useRef();

  const invoiceNo = `INV-${order._id.slice(-8).toUpperCase()}`;
  const billDate = new Date().toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
  const orderDate = new Date(order.orderDate).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });

  const originalPrice = Number(order.productId?.old_price) || 0;
  const discountPct = Number(order.productId?.new_price) || 0;
  const total = order.totalPrice;
  // derive unit price from actual total if product pricing fields are missing
  const unitPrice = originalPrice > 0
    ? originalPrice - (originalPrice * discountPct) / 100
    : total / (order.quantity || 1);
  const subtotal = total;
  const discountAmt = originalPrice > 0
    ? originalPrice * order.quantity - subtotal
    : 0;

  const fullAddress = [
    order.address,
    order.city,
    order.state,
    order.country,
    order.pincode,
  ]
    .filter(Boolean)
    .join(", ");

  const handlePrint = () => {
    const logoSrc = logo;
    const statusCol = statusColor[order.status] || "#374151";

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Invoice ${invoiceNo}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #1f2937; font-size: 13px; }
    .page { max-width: 740px; margin: 0 auto; padding: 36px 40px; }

    /* Header */
    .inv-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; }
    .brand { display: flex; align-items: center; gap: 12px; }
    .brand img { width: 56px; height: 56px; object-fit: contain; }
    .brand-name { font-size: 20px; font-weight: 800; color: #4c1d95; }
    .brand-sub { font-size: 11px; color: #6b7280; margin-top: 2px; }
    .inv-label { text-align: right; }
    .inv-label h2 { font-size: 26px; font-weight: 900; color: #4c1d95; letter-spacing: 1px; }
    .inv-label p { font-size: 12px; color: #6b7280; margin-top: 4px; }

    /* Divider */
    hr { border: none; border-top: 2px solid #ede9fe; margin: 18px 0; }

    /* Meta row */
    .meta-row { display: flex; justify-content: space-between; margin-bottom: 24px; }
    .meta-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px 18px; width: 48%; }
    .meta-box h4 { font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 8px; }
    .meta-box p { font-size: 13px; font-weight: 600; color: #1f2937; line-height: 1.7; }
    .meta-box .small { font-size: 12px; color: #6b7280; font-weight: 400; }

    /* Table */
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    .items-table thead tr { background: linear-gradient(to right, #6b46c1, #4c51bf); color: #fff; }
    .items-table th { padding: 11px 14px; text-align: left; font-size: 12px; font-weight: 700; letter-spacing: 0.3px; }
    .items-table td { padding: 12px 14px; border-bottom: 1px solid #f3f4f6; font-size: 13px; }
    .items-table tbody tr:last-child td { border-bottom: none; }
    .product-td { display: flex; align-items: center; gap: 10px; }
    .product-td img { width: 44px; height: 44px; object-fit: cover; border-radius: 8px; border: 1px solid #e5e7eb; }
    .p-id { font-size: 11px; color: #9ca3af; }
    .p-name { font-size: 13px; font-weight: 600; color: #1f2937; }
    .strike { text-decoration: line-through; color: #9ca3af; font-size: 11px; }
    .disc-pill { display: inline-block; background: #dcfce7; color: #15803d; padding: 2px 7px; border-radius: 12px; font-size: 11px; font-weight: 700; margin-top: 2px; }

    /* Totals */
    .totals-wrap { display: flex; justify-content: flex-end; }
    .totals-box { width: 260px; background: #f9fafb; border-radius: 12px; border: 1px solid #e5e7eb; overflow: hidden; }
    .totals-row { display: flex; justify-content: space-between; padding: 9px 16px; font-size: 13px; border-bottom: 1px solid #f3f4f6; }
    .totals-row:last-child { border-bottom: none; }
    .totals-row.total-final { background: linear-gradient(to right, #6b46c1, #4c51bf); color: #fff; font-size: 15px; font-weight: 800; }
    .label-grey { color: #6b7280; }
    .discount-val { color: #16a34a; font-weight: 700; }

    /* Status */
    .status-row { margin: 20px 0; display: flex; align-items: center; gap: 10px; }
    .status-pill { padding: 5px 14px; border-radius: 20px; font-size: 13px; font-weight: 700; border: 2px solid; }

    /* Footer */
    .inv-footer { margin-top: 28px; text-align: center; padding-top: 16px; border-top: 2px dashed #e5e7eb; }
    .inv-footer p { font-size: 12px; color: #9ca3af; line-height: 1.8; }
    .inv-footer .thank { font-size: 15px; font-weight: 700; color: #4c1d95; margin-bottom: 6px; }
  </style>
</head>
<body>
<div class="page">

  <div class="inv-header">
    <div class="brand">
      <img src="${logoSrc}" alt="logo" />
      <div>
        <div class="brand-name">Aavryaa FabHub</div>
        <div class="brand-sub">Premium Fabrics &amp; Fashion</div>
      </div>
    </div>
    <div class="inv-label">
      <h2>INVOICE</h2>
      <p>${invoiceNo}</p>
      <p>Bill Date: ${billDate}</p>
      <p>Order Date: ${orderDate}</p>
    </div>
  </div>

  <hr />

  <div class="meta-row">
    <div class="meta-box">
      <h4>Bill To</h4>
      <p>${order.name || "—"}</p>
      <p class="small">${order.email || ""}</p>
      <p class="small">${order.phoneNumber || ""}</p>
    </div>
    <div class="meta-box">
      <h4>Delivery Address</h4>
      <p>${fullAddress || "—"}</p>
    </div>
  </div>

  <table class="items-table">
    <thead>
      <tr>
        <th>#</th>
        <th>Product</th>
        <th>Unit Price</th>
        <th>Qty</th>
        <th>Amount</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>1</td>
        <td>
          <div class="product-td">
            <img src="${order.productId?.images?.[0] || ""}" alt="" />
            <div>
              <div class="p-id">${order.productId?.unique_id || ""}</div>
              <div class="p-name">${order.productId?.name || "Product"}</div>
              ${discountPct > 0 ? `<div><span class="disc-pill">-${discountPct}% off</span></div>` : ""}
            </div>
          </div>
        </td>
        <td>
          ${discountPct > 0 ? `<div class="strike">₹${originalPrice.toFixed(2)}</div>` : ""}
          <div>₹${unitPrice.toFixed(2)}</div>
        </td>
        <td>${order.quantity}</td>
        <td><strong>₹${subtotal.toFixed(2)}</strong></td>
      </tr>
    </tbody>
  </table>

  <div class="totals-wrap">
    <div class="totals-box">
      ${discountAmt > 0 ? `
      <div class="totals-row">
        <span class="label-grey">Subtotal (MRP)</span>
        <span>₹${(originalPrice * order.quantity).toFixed(2)}</span>
      </div>
      <div class="totals-row">
        <span class="label-grey">Discount</span>
        <span class="discount-val">- ₹${discountAmt.toFixed(2)}</span>
      </div>` : ""}
      <div class="totals-row total-final">
        <span>Total Payable</span>
        <span>₹${total.toFixed(2)}</span>
      </div>
    </div>
  </div>

  <div class="status-row">
    <span>Order Status:</span>
    <span class="status-pill" style="color:${statusCol}; border-color:${statusCol};">${order.status}</span>
  </div>

  <div class="inv-footer">
    <p class="thank">Thank you for shopping with Aavryaa FabHub! 🎉</p>
    <p>This is a computer-generated invoice and does not require a signature.</p>
    <p>For queries, contact us at support@aavryaafabhub.com</p>
  </div>

</div>
</body>
</html>`;

    const win = window.open("", "_blank", "width=800,height=900");
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  };

  return (
    <div className="bill-overlay" onClick={onClose}>
      <div className="bill-modal" onClick={(e) => e.stopPropagation()}>

        {/* Top action bar */}
        <div className="bill-topbar">
          <span className="bill-topbar-title">Delivery Invoice Preview</span>
          <div className="bill-topbar-actions">
            <button className="bill-print-btn" onClick={handlePrint}>
              🖨&nbsp; Print / Download
            </button>
            <button className="bill-close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Invoice document (preview) */}
        <div className="bill-document" ref={billRef}>

          {/* Header */}
          <div className="bill-header">
            <div className="bill-brand">
              <img src={logo} alt="logo" className="bill-logo" />
              <div>
                <div className="bill-brand-name">Aavryaa FabHub</div>
                <div className="bill-brand-sub">Premium Fabrics &amp; Fashion</div>
              </div>
            </div>
            <div className="bill-inv-meta">
              <div className="bill-inv-label">INVOICE</div>
              <div className="bill-inv-no">{invoiceNo}</div>
              <div className="bill-inv-date">Bill Date: {billDate}</div>
              <div className="bill-inv-date">Order Date: {orderDate}</div>
            </div>
          </div>

          <div className="bill-divider" />

          {/* Customer + Address */}
          <div className="bill-info-row">
            <div className="bill-info-box">
              <div className="bill-info-title">Bill To</div>
              <div className="bill-info-name">{order.name || "—"}</div>
              <div className="bill-info-sub">{order.email || ""}</div>
              <div className="bill-info-sub">{order.phoneNumber || ""}</div>
            </div>
            <div className="bill-info-box">
              <div className="bill-info-title">Delivery Address</div>
              <div className="bill-info-name" style={{ fontSize: 13 }}>{fullAddress || "—"}</div>
            </div>
          </div>

          {/* Items table */}
          <table className="bill-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Unit Price</th>
                <th>Qty</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>
                  <div className="bill-product-cell">
                    <img
                      src={order.productId?.images?.[0]}
                      alt={order.productId?.name}
                      className="bill-product-img"
                    />
                    <div>
                      <div className="bill-product-id">{order.productId?.unique_id}</div>
                      <div className="bill-product-name">{order.productId?.name || "Product"}</div>
                      {discountPct > 0 && (
                        <span className="bill-discount-pill">-{discountPct}% off</span>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  {discountPct > 0 && (
                    <div className="bill-strike">₹{originalPrice.toFixed(2)}</div>
                  )}
                  <div className="bill-unit-price">₹{unitPrice.toFixed(2)}</div>
                </td>
                <td>{order.quantity}</td>
                <td className="bill-amount">₹{subtotal.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          {/* Totals */}
          <div className="bill-totals-wrap">
            <div className="bill-totals-box">
              {discountAmt > 0 && (
                <>
                  <div className="bill-totals-row">
                    <span className="bill-totals-label">Subtotal (MRP)</span>
                    <span>₹{(originalPrice * order.quantity).toFixed(2)}</span>
                  </div>
                  <div className="bill-totals-row">
                    <span className="bill-totals-label">Discount</span>
                    <span className="bill-discount-val">− ₹{discountAmt.toFixed(2)}</span>
                  </div>
                </>
              )}
              <div className="bill-totals-row bill-totals-final">
                <span>Total Payable</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="bill-status-row">
            <span className="bill-status-label">Order Status:</span>
            <span
              className="bill-status-pill"
              style={{ color: statusColor[order.status] || "#374151", borderColor: statusColor[order.status] || "#374151" }}
            >
              {order.status}
            </span>
          </div>

          {/* Footer */}
          <div className="bill-footer">
            <div className="bill-footer-thank">Thank you for shopping with Aavryaa FabHub! 🎉</div>
            <div className="bill-footer-note">
              This is a computer-generated invoice and does not require a signature.
            </div>
            <div className="bill-footer-note">
              For queries, contact us at support@aavryaafabhub.com
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default DeliveryBill;
